"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Draft", to: "Open, Cancelled" },
  {
    from: "Open, Partially Paid, Paid, Overdue",
    to: "Cancelled only. You never Mark As Paid, Partially Paid, or Overdue — payments, debit notes, and due dates set those",
  },
  {
    from: "Cancelled",
    to: "Nothing. Mark Bill As is hidden. Cancelled is a dead end",
  },
];

const CONVERT_FROM = [
  {
    from: "Purchase Order",
    when: "Accepted or Partially Recorded, and no non-cancelled GRPO exists for those POs. Convert To Bills on the PO",
    result:
      "Vendor, items, remaining quantity come across. Location often locked. If a live GRPO already exists, this path is refused — bill from the GRPO",
  },
  {
    from: "GRPO",
    when: "Recorded only. Convert To Bill on the GRPO",
    result:
      "Received lines come across. Saving this bill moves the GRPO to Converted. Stock may already be on the GRPO depending on Inventory Config",
  },
  {
    from: "Scratch",
    when: "Add Bill with nothing pre-filled",
    result: "A new bill. Save makes Draft; Save & Open makes Open",
  },
  {
    from: "Duplicate",
    when: "Duplicate Document on the list or detail",
    result:
      "Opens Add with the same vendor, items, and amounts. New bill number, today's dates, Draft. Due date recalculates from payment terms. No GRPO / PO link copied as a live conversion",
  },
  {
    from: "Recurring Bill",
    when: "Daily job or Create Bill retry on a FAILED cycle",
    result: "Always lands as Draft here, with a comment that it came from the recurring bill",
  },
];

const CONVERT_TO = [
  {
    to: "Payment Made",
    when: "Balance greater than zero. The payment picker lists Open, Partially Paid, or Overdue — not Draft",
    where: "Payments Made → Add. There is no Convert button on the bill",
    result:
      "Approving the payment moves this bill to Partially Paid or Paid. You do not Mark As those statuses yourself",
  },
  {
    to: "Apply Credit",
    when: "This bill still has a balance, and the vendor's Opening Balance on this bill's branch is negative (remaining credit). The button is on the bill detail",
    where:
      "Apply Credit on the bill detail. Modal: Apply Opening Balance to Bills",
    result:
      "This bill is pre-selected. It becomes Partially Paid or Paid without creating a Payment Made. Payment History shows Opening Balance — Credit",
  },
  {
    to: "Debit Note",
    when: "A debit note can name this bill, or be applied to it later",
    where: "Debit Notes module — not a Convert button on the bill",
    result:
      "Reduces what is still due. Applied Credits appear on Payment History. A debit note on this bill blocks cancel and delete",
  },
];

const RULES = [
  {
    topic: "Ledger posts on save, including Draft",
    rule: "A bill posts to the general ledger as soon as it is saved. Cancelled reverses that journal. Moving Open → Partially Paid → Paid does not re-post the bill journal",
  },
  {
    topic: "Subscription gate",
    rule: "Creating a bill is limited by your plan. Editing, status, duplicating, Apply Credit, and deleting are not separately gated",
  },
  {
    topic: "No primary-bank gate",
    rule: "You can create a bill without a primary bank",
  },
  {
    topic: "Bill number",
    rule: "Auto-generated unless you turn that off under Bill Configuration on the form (or Preferences on the list). That same drawer stores Terms & Conditions. Locked after save",
  },
  {
    topic: "Division, Branch, Warehouse, Vendor",
    rule: "All four are required to create. After save they are locked. Converted-from PO or GRPO also lock those fields on the new form before save",
  },
  {
    topic: "Currency",
    rule: "Taken from the vendor, or from the PO / GRPO if this bill came from one. An exchange-rate field appears when that currency differs from the company base",
  },
  {
    topic: "Stock Add Source is a company setting",
    rule: "Settings → Inventory Config → Purchase Flow Stock Add Source. Set it to Bill for this module to add warehouse quantity. The choice is stored on each bill at create time",
  },
  {
    topic: "Stock add on this bill",
    rule: "Source = Bill: scratch / PO bills add stock by default (shipping-to-customer can force it off). Source = GRPO: a bill created from a GRPO does not add stock — the GRPO already did; ledger settles GRNI. When source is GRPO and you add a bill from scratch or PO, the form asks whether to Manage inventory effect (add stock from this bill). Inventory Impact on detail shows Stock Add Enabled/Disabled from create time",
  },
  {
    topic: "You can only edit a Draft",
    rule: "Open, Partially Paid, Paid, Overdue, and Cancelled cannot be edited in the UI. Vendor and location stay locked even on a Draft",
  },
  {
    topic: "Paid / Partially Paid / Overdue are not Mark As",
    rule: "Mark Bill As on Draft offers Open and Cancelled. On every other live status it only offers Cancelled. Payments, debit notes, and vendor credit set Paid / Partially Paid. Overdue follows the due date",
  },
  {
    topic: "Cancelled is a dead end",
    rule: "A Cancelled bill cannot be brought back to Draft or Open",
  },
  {
    topic: "Cancelling vs deleting",
    rule: "Cancel is blocked by an active Payment Made, a Debit Note created from or applied to this bill, or opening-balance vendor-credit applications. Delete is blocked by Payment Made / Debit Note, and by opening-balance credit rows — remove Opening Balance — Credit from Payment History first. Stock reverses on cancel/delete if this bill had added stock",
  },
  {
    topic: "Apply Credit on the bill detail",
    rule: "Shows when this bill still has a balance and the vendor's Opening Balance on this bill's branch is still negative. It is vendor opening-balance credit — not Apply to Bill on a Debit Note. The Bills list TOOLS menu also has Auto Map Opening Balance, which maps remaining vendor credit across vendors onto open bills, oldest due date first",
  },
  {
    topic: "No e-invoice",
    rule: "Bills have no IRN or e-way bill",
  },
  {
    topic: "Attachments",
    rule: "Up to 3 files, 5 MB each",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a bill" },
      { heading: "2. Stock add — Inventory Config" },
      { heading: "3. Viewing, searching, and exporting" },
      { heading: "4. Editing a bill" },
      { heading: "5. Printing and downloading" },
      { heading: "6. Changing a bill's status" },
      { heading: "7. Converted from — what can become a Bill" },
      { heading: "8. Converted to — what follows a Bill" },
      { heading: "9. Apply Credit — opening-balance credit on this bill" },
      { heading: "10. Duplicating a bill" },
      { heading: "11. Deleting a bill" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function BillsPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Bills
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone recording what you owe a vendor — then paying it,
            applying vendor credit, or raising a debit note
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Bill</B> is the vendor&apos;s invoice in Books.{" "}
            <B>Save</B> makes <B>Draft</B> and already posts the ledger and
            updates Current Balance. <B>Save &amp; Open</B> makes{" "}
            <B>Open</B>. There is no e-invoice on this module.{" "}
            <B>Apply Credit</B> on the bill header is this vendor&apos;s
            negative opening balance — not a Debit Note.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a bill
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick Division, Branch, Warehouse. On Add: <B>Save</B> (Draft)
            and <B>Save &amp; Open</B>. Edit is Save only.
          </p>
          <Definitions
            items={[
              {
                term: "Bill number",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Bill Configuration</B> on the form. List{" "}
                    <B>Preferences</B> opens the same drawer. Terms &amp;
                    Conditions live there. Locked after save.
                  </>
                ),
              },
              {
                term: "Vendor, payment terms, due date",
                desc: (
                  <>
                    Vendor required. Payment terms copy from the vendor.
                    Due date follows those terms.
                  </>
                ),
              },
              {
                term: "Items, discounts, charges, tax",
                desc: (
                  <>
                    Purchasable items. Settings → Discounts and Settings →
                    Additional charges apply the same way as other purchase
                    documents. Converted-from GRPO shows GRPO No#; from PO
                    shows the PO numbers.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Add Bill with Save and Save & Open, Bill Configuration on the number field." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Stock add — Inventory Config
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Same company switch as GRPO: Purchase Flow Stock Add Source.
          </p>
          <Definitions
            items={[
              {
                term: "Source = Bill",
                desc: (
                  <>
                    Scratch and PO bills add stock (batches) unless shipping
                    is to a customer. A bill from a GRPO can still exist;
                    stock may already have been on the GRPO if that setting
                    was GRPO at receive time.
                  </>
                ),
              },
              {
                term: "Source = GRPO",
                desc: (
                  <>
                    Bill from GRPO does <B>not</B> add stock. Checkbox
                    Manage inventory effect is hidden. Ledger settles GRNI
                    for those lines. Scratch / PO bills can still tick{" "}
                    <B>Manage inventory effect (add stock from this bill)</B>
                    after a confirm modal.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            Convert-from PO or GRPO is not the same as Stock Add Source.
            You can still bill a GRPO when stock was added on the GRPO.
          </Callout>
          <Screenshot caption="Screenshot: Inventory Impact on bill detail — Stock Add Enabled or Disabled." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Viewing, searching, and exporting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Status tabs (Draft, Open, Partially Paid, Paid, Overdue,
            Cancelled). Toolbar: <B>Add</B>; TOOLS →{" "}
            <B>Auto Map Opening Balance</B>;{" "}
            <B>Export Bills</B>; Preferences; custom fields, refresh, reset
            columns.
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Detail: View Vendor, Apply Credit (conditional), Download,
            Print. Tabs: overview, attachments, stock movement,{" "}
            <B>Payment History</B> (Payment Made, Applied Credits from
            debit notes, Opening Balance — Credit), comments.
          </p>
          <Screenshot caption="Screenshot: Bills list with Auto Map Opening Balance, Export Bills, and status tabs." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Editing a bill
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Draft only in the UI. Vendor and location stay locked.
          </p>

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Printing and downloading
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Detail: <B>Download</B> and <B>Print</B>.
          </p>

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Changing a bill&apos;s status
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Button: <B>Mark Bill As</B>.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Mark As
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((t, i) => (
                  <tr
                    key={t.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[240px]">
                      {t.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: Mark Bill As on a Draft — Open and Cancelled." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Converted from — what can become a Bill
          </h3>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONVERT_FROM.map((r, i) => (
                  <tr
                    key={r.from}
                    className={
                      i < CONVERT_FROM.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top">
                      {r.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Converted to — what follows a Bill
          </h3>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    To
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Where
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONVERT_TO.map((r, i) => (
                  <tr
                    key={r.to}
                    className={
                      i < CONVERT_TO.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top">
                      {r.to}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.where}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: Payment History on a bill with Payment Made, Applied Credits, and Opening Balance — Credit." />

          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Apply Credit — opening-balance credit on this bill
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Same idea as Apply Credit on an invoice, flipped to the vendor.
            Needs remaining bill balance, vendor Opening Balance still
            negative on <B>this bill&apos;s branch</B>, and Bills edit —
            not view-only.
          </p>
          <Callout type="warning">
            Apply Credit here is not Apply to Bill on a Debit Note. Debit
            notes use that note&apos;s available balance. This button uses
            remaining vendor opening-balance credit.
          </Callout>
          <Screenshot caption="Screenshot: Apply Credit on bill detail — Apply Opening Balance to Bills, branch locked." />

          <h3
            id="section-0-sub-9"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            10. Duplicating a bill
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            <B>Duplicate Document</B>. New number, Draft, due date from
            payment terms.
          </p>

          <h3
            id="section-0-sub-10"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            11. Deleting a bill
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Remove Payment Made, Debit Note, and Opening Balance — Credit
            rows first. Cancelled cannot be uncancelled — delete is a
            separate action with the same blockers, then the record is gone.
            Linked PO / GRPO remaining quantity is recalculated.
          </p>
          <Screenshot caption="Screenshot: delete blocked until Opening Balance — Credit is removed from Payment History." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/purchase/grpo"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              GRPO <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              A Recorded GRPO converts only into this Bill
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/payments-made"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Payments Made <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              How a bill becomes Partially Paid or Paid
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/debit-notes"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Debit Notes <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Apply to Bill uses debit-note balance, not this header
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/vendors"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Vendors <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where Opening Balance is set per branch, and Pay Against Bill
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
